import { useState } from "react";
import { readProjectOptions, type ProjectOptionsForm } from "../model/inp/options.ts";
import { FLOW_UNIT_TOKENS } from "../model/units.ts";
import { useT } from "../i18n/hooks.ts";
import { Dialog } from "./Dialog.tsx";
import { DialogActions } from "./DialogActions.tsx";

const FLOW_ROUTING = ["STEADY", "KINWAVE", "DYNWAVE"] as const;
const NORMAL_FLOW_LIMITED = ["SLOPE", "FROUDE", "BOTH", "NONE"] as const;

function toNativeDate(swmmDate: string): string {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(swmmDate.trim());
  if (!m) return "";
  const [, mm, dd, yyyy] = m;
  return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
}

function fromNativeDate(nativeDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(nativeDate);
  if (!m) return "";
  const [, yyyy, mm, dd] = m;
  return `${mm}/${dd}/${yyyy}`;
}

// No separate fromNativeTime: a native time input's own output is already
// zero-padded HH:MM:SS, exactly SWMM's format, so it's used as-is.
function toNativeTime(swmmTime: string): string {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(swmmTime.trim());
  if (!m) return "";
  const [, h, mi, s = "00"] = m;
  return `${h.padStart(2, "0")}:${mi}:${s}`;
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="options-field">
      {label}
      <input
        type="date"
        value={toNativeDate(value)}
        onChange={(e) => onChange(fromNativeDate(e.target.value))}
      />
    </label>
  );
}

function TimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="options-field">
      {label}
      <input
        type="time"
        step={1}
        value={toNativeTime(value)}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

// Empty string means "use the engine's hardcoded default" — the number is
// shown only as a placeholder, never pre-filled as a value, so the user
// can't mistake it for the current setting.
function NumberField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <label className="options-field">
      {label}
      <input
        type="number"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

type OptionsTab = "general" | "period" | "timesteps" | "dynamicwave";

// All 4 tabs' fields live in the same `form` object regardless of which
// tab is showing, so switching tabs never loses an edit made on another.
export function OptionsDialog({
  rawSections,
  onSave,
  onClose,
}: {
  rawSections: ReadonlyMap<string, string[][]>;
  onSave: (form: ProjectOptionsForm) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [tab, setTab] = useState<OptionsTab>("general");
  const [form, setForm] = useState<ProjectOptionsForm>(() => readProjectOptions(rawSections));

  function set<K extends keyof ProjectOptionsForm>(field: K, value: ProjectOptionsForm[K]) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  // Every tab's <section> stays mounted; this just picks the class that
  // makes the non-active ones invisible.
  function sectionClass(key: OptionsTab): string {
    return tab === key ? "options-section" : "options-section options-section-inactive";
  }

  const tabs: { key: OptionsTab; label: string }[] = [
    { key: "general", label: t("optionsDialog.general") },
    { key: "period", label: t("optionsDialog.simulationPeriod") },
    { key: "timesteps", label: t("optionsDialog.timeSteps") },
    { key: "dynamicwave", label: t("optionsDialog.dynamicWave") },
  ];

  return (
    <Dialog title={t("optionsDialog.title")} onClose={onClose} wide fitContent>
      <div className="project-data-tabs" role="tablist">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            className={tab === tb.key ? "button tab-active" : "button"}
            role="tab"
            aria-selected={tab === tb.key}
            onClick={() => setTab(tb.key)}
          >
            {tb.label}
          </button>
        ))}
      </div>
      <div className="options-dialog-body">
        <section className={sectionClass("general")}>
          <label className="options-field">
            {t("optionsDialog.flowUnits")}
            <select value={form.flowUnits} onChange={(e) => set("flowUnits", e.target.value)}>
              {FLOW_UNIT_TOKENS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </label>
          <label className="options-field">
            {t("optionsDialog.flowRouting")}
            <select value={form.flowRouting} onChange={(e) => set("flowRouting", e.target.value)}>
              {FLOW_ROUTING.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <NumberField
            label={t("optionsDialog.minSlope")}
            value={form.minSlope}
            onChange={(v) => set("minSlope", v)}
            placeholder="0"
          />
        </section>

        <section className={sectionClass("period")}>
          <DateField
            label={t("optionsDialog.startDate")}
            value={form.startDate}
            onChange={(v) => set("startDate", v)}
          />
          <TimeField
            label={t("optionsDialog.startTime")}
            value={form.startTime}
            onChange={(v) => set("startTime", v)}
          />
          <DateField
            label={t("optionsDialog.endDate")}
            value={form.endDate}
            onChange={(v) => set("endDate", v)}
          />
          <TimeField
            label={t("optionsDialog.endTime")}
            value={form.endTime}
            onChange={(v) => set("endTime", v)}
          />
          <DateField
            label={t("optionsDialog.reportStartDate")}
            value={form.reportStartDate}
            onChange={(v) => set("reportStartDate", v)}
          />
          <TimeField
            label={t("optionsDialog.reportStartTime")}
            value={form.reportStartTime}
            onChange={(v) => set("reportStartTime", v)}
          />
        </section>

        <section className={sectionClass("timesteps")}>
          <TimeField
            label={t("optionsDialog.reportStep")}
            value={form.reportStep}
            onChange={(v) => set("reportStep", v)}
          />
          <TimeField
            label={t("optionsDialog.wetStep")}
            value={form.wetStep}
            onChange={(v) => set("wetStep", v)}
          />
          <TimeField
            label={t("optionsDialog.dryStep")}
            value={form.dryStep}
            onChange={(v) => set("dryStep", v)}
          />
          <TimeField
            label={t("optionsDialog.routingStep")}
            value={form.routingStep}
            onChange={(v) => set("routingStep", v)}
          />
        </section>

        <section className={sectionClass("dynamicwave")}>
          <label className="options-field">
            {t("optionsDialog.normalFlowLimited")}
            <select
              value={form.normalFlowLimited}
              onChange={(e) => set("normalFlowLimited", e.target.value)}
            >
              {NORMAL_FLOW_LIMITED.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="options-field options-field-checkbox">
            <input
              type="checkbox"
              checked={form.allowPonding === "YES"}
              onChange={(e) => set("allowPonding", e.target.checked ? "YES" : "NO")}
            />
            {t("optionsDialog.allowPonding")}
          </label>
          <NumberField
            label={t("optionsDialog.variableStep")}
            value={form.variableStep}
            onChange={(v) => set("variableStep", v)}
            placeholder="0.75"
          />
          <NumberField
            label={t("optionsDialog.minimumStep")}
            value={form.minimumStep}
            onChange={(v) => set("minimumStep", v)}
            placeholder="0.5"
          />
          <NumberField
            label={t("optionsDialog.maxTrials")}
            value={form.maxTrials}
            onChange={(v) => set("maxTrials", v)}
            placeholder="8"
          />
          <NumberField
            label={t("optionsDialog.headTolerance")}
            value={form.headTolerance}
            onChange={(v) => set("headTolerance", v)}
            placeholder="0.005"
          />
          <NumberField
            label={t("optionsDialog.minSurfArea")}
            value={form.minSurfArea}
            onChange={(v) => set("minSurfArea", v)}
            placeholder="12.566"
          />
          <TimeField
            label={t("optionsDialog.lengtheningStep")}
            value={form.lengtheningStep}
            onChange={(v) => set("lengtheningStep", v)}
          />
        </section>
      </div>
      <DialogActions
        primary={{ label: t("common.save"), onClick: () => onSave(form) }}
        onClose={onClose}
      />
    </Dialog>
  );
}
